import React, { useRef, useState, useEffect } from 'react';
import {
  FileSpreadsheet,
  Download,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Loader2,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API_URL } from '../config';
import { getCandidateResume } from '../services/onboardingapi';
import marabesLogo from '../assets/images/logos/Marabes.png';
import axopeopleLogo from '../assets/images/logos/Axopeople.png';
import { getVariableValue } from 'framer-motion';
import { getFormHelperTextUtilityClasses } from '@mui/material';
import RichTextEditor from './RichTextEditor';

const getBase64FromUrl = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const PLANNED_STATUSES = ['1st Interview (Online)', '2nd Interview', '3rd Interview'];

const POSITION_OPTIONS = [
  'HR Manager', 'Project Manager', 'Customer Service', 'HR', 'Manager',
  'Account Manager', 'Quality Reviewer', 'Bookkeeper', 'Planner', 'Marketing',
  'Personal Assistance', 'Software Engineer', 'Client Adviser', 'IT Professional'
];

const CandidateOverviewTable = ({ candidates = [], onCandidateUpdate }) => {
  const [isDark, setIsDark] = useState(() => document.body.classList.contains('dark'));
  const [searchTerm, setSearchTerm] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [fitMode, setFitMode] = useState(true);
  const [interviewSubFilter, setInterviewSubFilter] = useState(null); // null | 'first' | 'second'
  const [editingField, setEditingField] = useState(null); // { row, field, label, value, cellKey }
  const tableScrollRef = useRef(null);

  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.body.classList.contains('dark'))
    );
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const handleDownloadResume = async (candidateId) => {
    try {
      const resumeBlob = await getCandidateResume(candidateId);
      const url = window.URL.createObjectURL(new Blob([resumeBlob], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // Optional: Revoke the object URL after a delay if needed, 
      // but if we revoke immediately, the new tab might fail to load it.
    } catch (error) {
      console.error('Error downloading resume:', error);
    }
  };

  const dm = {
    card: isDark ? '#1e293b' : 'var(--card-bg, white)',
    surface: isDark ? '#0f172a' : '#F8FAFC',
    border: isDark ? 'rgba(255,255,255,0.10)' : '#E2E8F0',
    textPrimary: isDark ? '#f1f5f9' : '#0F172A',
    textSecondary: isDark ? '#94a3b8' : '#64748B',
    primary: '#0EA5E9',
    primaryLight: isDark ? 'rgba(14, 165, 233, 0.15)' : '#E0F2FE',
    success: '#10B981',
    successLight: isDark ? 'rgba(16, 185, 129, 0.15)' : '#D1FAE5',
    warning: '#F59E0B',
    warningLight: isDark ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
    headerBg: isDark ? '#334155' : '#F1F5F9',
    rowHover: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
  };

  const getStatusBadge = (status, level = null, row = null) => {
    if (!status) return null;
    let color, bg, Icon;
    if (status.includes('Hired') || status.includes('Approved')) {
      color = dm.success;
      bg = dm.successLight;
      Icon = CheckCircle;
    } else if (status.includes('Declined') || status.includes('Rejected')) {
      color = '#EF4444';
      bg = isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2';
      Icon = XCircle;
    } else {
      color = dm.warning;
      bg = dm.warningLight;
      Icon = Clock;
    }

    const isHired = status.includes('Hired') || status.includes('Approved');
    const effectiveLevel = level || row?.level || row?.candidate?.level;

    const levelConfig = {
      HIGH: {
        bg: isDark ? 'rgba(22, 101, 52, 0.35)' : '#DCFCE7',
        color: isDark ? '#86EFAC' : '#15803D',
        border: isDark ? '#16A34A' : '#86EFAC',
        circle: '🟢',
        mark: 'HIGH',
      },
      AVERAGE: {
        bg: isDark ? 'rgba(194, 65, 12, 0.35)' : '#FFEDD5',
        color: isDark ? '#FDBA74' : '#C2410C',
        border: isDark ? '#EA580C' : '#FDBA74',
        circle: '🟠',
        mark: 'AVERAGE',
      },
      LOW: {
        bg: isDark ? 'rgba(161, 98, 7, 0.35)' : '#FEF9C3',
        color: isDark ? '#FEF08A' : '#854D0E',
        border: isDark ? '#CA8A04' : '#FDE047',
        circle: '🟡',
        mark: 'LOW',
      },
    };

    const currentLevelStyle = effectiveLevel ? levelConfig[effectiveLevel] : null;

    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
          padding: fitMode ? '0.28rem 0.45rem' : '0.35rem 0.75rem', borderRadius: '2rem',
          backgroundColor: bg, color: color, fontSize: fitMode ? '0.68rem' : '0.85rem', fontWeight: '600',
          whiteSpace: 'nowrap'
        }}>
          <Icon size={14} />
          {status}
        </span>
        {isHired && (
          row && onCandidateUpdate ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
              <select
                value={effectiveLevel || ''}
                onChange={(e) => commitCandidateField(row, { level: e.target.value || null }, 'level')}
                style={{
                  fontSize: fitMode ? '0.62rem' : '0.72rem',
                  fontWeight: '700',
                  padding: '0.12rem 0.35rem',
                  borderRadius: '1rem',
                  border: `1px solid ${currentLevelStyle ? currentLevelStyle.border : dm.border}`,
                  backgroundColor: currentLevelStyle ? currentLevelStyle.bg : dm.card,
                  color: currentLevelStyle ? currentLevelStyle.color : dm.textSecondary,
                  cursor: 'pointer',
                  outline: 'none',
                  textAlign: 'center'
                }}
                title="Candidate Level (HIGH: 🟢, AVERAGE: 🟠, LOW: 🟡)"
              >
                <option value="">No Level</option>
                <option value="HIGH">🟢 HIGH</option>
                <option value="AVERAGE">🟠 AVERAGE</option>
                <option value="LOW">🟡 LOW</option>
              </select>
              {renderSavingIndicator(row.id, 'level')}
            </div>
          ) : currentLevelStyle ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.12rem 0.45rem', borderRadius: '1rem',
              backgroundColor: currentLevelStyle.bg, color: currentLevelStyle.color,
              border: `1px solid ${currentLevelStyle.border}`,
              fontSize: fitMode ? '0.62rem' : '0.72rem', fontWeight: '700',
              letterSpacing: '0.025em', whiteSpace: 'nowrap'
            }}>
              <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>{currentLevelStyle.circle}</span>
              {currentLevelStyle.mark}
            </span>
          ) : null
        )}
      </div>
    );
  };

  const commitCandidateField = async (row, updates, cellKey) => {
    if (!onCandidateUpdate || !row?.candidate) return;
    setSavingCell(`${row.id}-${cellKey}`);
    try {
      await onCandidateUpdate(row.candidate, updates);
    } catch (error) {
      console.error('Error updating candidate from overview table:', error);
    } finally {
      setSavingCell(null);
    }
  };

  const inputBaseStyle = {
    width: '100%',
    minWidth: 0,
    border: `1px solid ${dm.border}`,
    borderRadius: '0.35rem',
    padding: '0.35rem 0.45rem',
    backgroundColor: dm.card,
    color: dm.textPrimary,
    fontSize: fitMode ? '0.68rem' : '0.75rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const scrollToSection = (left) => {
    tableScrollRef.current?.scrollTo({ left, behavior: 'smooth' });
  };

  const renderSavingIndicator = (rowId, cellKey) => (
    savingCell === `${rowId}-${cellKey}` ? (
      <Loader2 size={12} color={dm.primary} style={{ marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    ) : null
  );

  const renderEditableText = (row, field, cellKey, { type = 'text', multiline = false } = {}) => {
    const value = row.candidate?.[field] ?? '';
    const displayValue = value === null || value === undefined ? '' : value;
    const handleBlur = (event) => {
      const nextValue = event.target.value;
      if (String(displayValue) !== nextValue) {
        commitCandidateField(row, { [field]: nextValue }, cellKey);
      }
    };

    if (multiline) {
      return (
        <textarea
          defaultValue={displayValue}
          onBlur={handleBlur}
          onFocus={(event) => {
            event.currentTarget.style.height = '6.5rem';
          }}
          rows={1}
          title={displayValue || '-'}
          style={{
            ...inputBaseStyle,
            height: '2rem',
            maxHeight: '6.5rem',
            resize: 'vertical',
            whiteSpace: 'normal',
            lineHeight: 1.35,
            overflow: 'auto'
          }}
        />
      );
    }

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
        <input
          type={type}
          defaultValue={displayValue}
          onBlur={handleBlur}
          title={displayValue || '-'}
          style={inputBaseStyle}
        />
        {renderSavingIndicator(row.id, cellKey)}
      </span>
    );
  };

  const renderEditableLink = (row, field, cellKey) => {
    const value = row.candidate?.[field] ?? '';
    const displayValue = value === null || value === undefined ? '' : value;
    const handleBlur = (event) => {
      const nextValue = event.target.value;
      if (String(displayValue) !== nextValue) {
        commitCandidateField(row, { [field]: nextValue }, cellKey);
      }
    };

    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.15rem', width: '100%' }}>
        <input
          type="url"
          defaultValue={displayValue}
          onBlur={handleBlur}
          title={displayValue || '-'}
          style={{ ...inputBaseStyle, flex: 1, minWidth: 0 }}
        />
        {displayValue && (
          <a
            href={displayValue.startsWith('http') ? displayValue : `https://${displayValue}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open Link"
            style={{ color: dm.primary, padding: '0 0.2rem', display: 'flex', alignItems: 'center' }}
          >
            <ExternalLink size={14} />
          </a>
        )}
        {renderSavingIndicator(row.id, cellKey)}
      </span>
    );
  };

  const renderEditableCheck = (row, field, activeValue, cellKey) => {
    const checked = row.candidate?.[field] === activeValue;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => commitCandidateField(row, { [field]: event.target.checked ? activeValue : '' }, cellKey)}
          title={checked ? 'Yes' : 'No'}
          style={{ width: '1rem', height: '1rem', accentColor: dm.primary, cursor: 'pointer' }}
        />
        {renderSavingIndicator(row.id, cellKey)}
      </span>
    );
  };

  const renderEditableSelect = (row, field, options, cellKey) => {
    const value = row.candidate?.[field] || '';
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <select
          value={value}
          onChange={(event) => commitCandidateField(row, { [field]: event.target.value }, cellKey)}
          title={value || '-'}
          style={{ ...inputBaseStyle, cursor: 'pointer', padding: '0.2rem' }}
        >
          <option value="">-</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {renderSavingIndicator(row.id, cellKey)}
      </span>
    );
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const sanitizeHtmlForTable = (html) => {
    if (!html) return '';
    let text = html.replace(/<p[^>]*>/gi, ' ').replace(/<\/p>/gi, ' ');
    text = text.replace(/<br\s*\/?>/gi, ' ');
    text = text.replace(/<[^>]+>/gi, match => {
      const m = match.toLowerCase();
      if (['<b>', '</b>', '<strong>', '</strong>', '<i>', '</i>', '<em>', '</em>', '<u>', '</u>'].includes(m)) return match;
      return '';
    });
    return text.trim();
  };

  // Converts HTML from RichTextEditor into structured plain text for PDF
  // Preserves line breaks and bullet points
  const htmlToPlainText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const lines = [];
    const walk = (node) => {
      if (node.nodeType === 3) {
        const t = node.nodeValue ? node.nodeValue.replace(/\u00A0/g, ' ') : '';
        if (t) lines.push(t);
      } else if (node.nodeType === 1) {
        const tag = node.tagName.toUpperCase();
        if (tag === 'BR') { lines.push('\n'); return; }
        if (tag === 'LI') lines.push('• ');
        node.childNodes.forEach(walk);
        if (['P','DIV','LI','H1','H2','H3','H4','H5','H6','UL','OL','TR'].includes(tag)) lines.push('\n');
      }
    };
    tmp.childNodes.forEach(walk);
    return lines.join('').replace(/\n{3,}/g, '\n\n').trim();
  };

  // Detects dominant style (bold/italic) from an HTML string for PDF cell styling
  const detectHtmlStyle = (html) => {
    if (!html) return null;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const hasBold = !!(tmp.querySelector('b, strong'));
    const hasItalic = !!(tmp.querySelector('i, em'));
    if (hasBold && hasItalic) return 'bolditalic';
    if (hasBold) return 'bold';
    if (hasItalic) return 'italic';
    return null;
  };

  // Converts HTML from RichTextEditor into ExcelJS richText array
  // Preserves bold, italic, underline, bullets, and line breaks
  const htmlToExcelRichText = (html) => {
    if (!html) return null;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const parts = [];
    const walk = (node, style) => {
      if (node.nodeType === 3) {
        if (node.nodeValue) parts.push({ text: node.nodeValue.replace(/\u00A0/g, ' '), font: { ...style } });
      } else if (node.nodeType === 1) {
        const tag = node.tagName.toUpperCase();
        const s = { ...style };
        if (tag === 'B' || tag === 'STRONG') s.bold = true;
        if (tag === 'I' || tag === 'EM') s.italic = true;
        if (tag === 'U') s.underline = true;
        if (tag === 'BR') { parts.push({ text: '\n', font: { ...style } }); return; }
        if (tag === 'LI') parts.push({ text: '• ', font: { ...style } });
        node.childNodes.forEach(child => walk(child, s));
        if (['P','DIV','LI','H1','H2','H3','H4'].includes(tag)) parts.push({ text: '\n', font: { ...style } });
      }
    };
    tmp.childNodes.forEach(child => walk(child, {}));
    const result = parts.filter(p => p.text);
    // Trim trailing newlines
    while (result.length && result[result.length - 1].text.trim() === '') result.pop();
    return result.length > 0 ? { richText: result } : null;
  };

  const renderEditableOverlay = (row, field, label, cellKey) => {
    const displayValue = row.candidate?.[field] || '';
    
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', height: '100%', width: '100%' }}>
        <div
          onClick={() => setEditingField({ row, field, label, value: displayValue, cellKey })}
          style={{
            cursor: 'pointer',
            padding: '0.35rem 0.45rem',
            backgroundColor: dm.card,
            border: `1px solid ${dm.border}`,
            borderRadius: '0.35rem',
            width: '100%',
            minHeight: '2rem',
            maxHeight: '4rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textAlign: 'left',
            fontSize: fitMode ? '0.68rem' : '0.75rem',
            color: displayValue ? dm.textPrimary : dm.textSecondary,
            boxSizing: 'border-box'
          }}
          title="Click to edit"
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlForTable(displayValue) || 'Click to edit...' }}
        />
        {renderSavingIndicator(row.id, cellKey)}
      </span>
    );
  };

  // Check if the incoming candidates include planned interview statuses
  const hasPlannedCandidates = candidates.some(c => PLANNED_STATUSES.includes(c.status));

  // Apply interview sub-filter before search
  const subFilteredCandidates = interviewSubFilter === 'first'
    ? candidates.filter(c => c.status === '1st Interview (Online)')
    : interviewSubFilter === 'second'
      ? candidates.filter(c => c.status === '2nd Interview')
      : candidates;

  // Process data for table
  const tableData = subFilteredCandidates.filter(c =>
    (c.firstName + ' ' + c.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.position && c.position.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.age != null && String(c.age).includes(searchTerm))
  ).map(c => {
    return {
      id: c.id || c.candidateId,
      candidate: c,
      fullName: `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      city: c.office || c.city || '-',
      age: c.age ?? '-',

      // Position
      bookkeeper: c.position === 'Bookkeeper',
      marketing: c.position === 'Marketing',
      cs: c.position === 'Customer Service',
      it: c.position === 'IT Professional',

      // Education
      master: c.diploma === 'Master',
      engineer: c.diploma === 'Engineer',
      bachelor: c.diploma === 'Bachelor',
      highschool: c.diploma === 'High School Diploma',
      technicien: c.diploma === 'Specialized Technician',

      // Professional Experience (stripped for UI display)
      jobs: c.experience ? stripHtml(c.experience) : '-',
      internship: c.internship ? stripHtml(c.internship) : '-',
      // Raw HTML preserved for rich exports
      jobsHtml: c.experience || '',
      internshipHtml: c.internship || '',

      // Applied on
      indeed: c.appliedFrom === 'Indeed',
      email: c.appliedFrom === 'Email',
      linkedin: c.appliedFrom === 'LinkedIn',

      // Resume & HR
      hasResume: !!c.resumeTeamsLink,
      resumeTeamsLink: c.resumeTeamsLink || '',
      portfolio: c.portfolioLink ? 'Link' : '-',
      portfolioLink: c.portfolioLink, // actual url for clicking

      // Feedback (stripped for UI display)
      feedback: c.description ? stripHtml(c.description) : '-',
      // Raw HTML preserved for rich exports
      feedbackHtml: c.description || '',

      status: c.status || 'Pending',
      level: c.level || null,
    };
  });

  const cityCounts = candidates.reduce((acc, c) => {
    const office = (c.office || c.city || '').toLowerCase();
    if (office.includes('nador')) acc.nador++;
    else if (office.includes('tangier') || office.includes('tanger')) acc.tangier++;
    else if (office) acc.other++;
    return acc;
  }, { nador: 0, tangier: 0, other: 0 });

  const eduCounts = candidates.reduce((acc, c) => {
    const diploma = (c.diploma || '').toLowerCase();
    if (diploma.includes('master') || diploma.includes('bac+5') || diploma.includes('bac + 5') || diploma.includes('bac+ 5') || diploma.includes('bac +5')) acc.master++;
    else if (diploma.includes('bachelor') || diploma.includes('bac+3') || diploma.includes('bac + 3') || diploma.includes('licence') || diploma.includes('bac+ 3') || diploma.includes('bac +3')) acc.bachelor++;
    else if (diploma) acc.other++;
    return acc;
  }, { master: 0, bachelor: 0, other: 0 });

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Overview 2026', { views: [{ showGridLines: false }] });

    // Header Styles
    const headerFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E293B' } // Dark blue
    };
    const headerFillDark = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF0F172A' } // Darker blue
    };
    const headerFont = { bold: true, color: { argb: 'FFFFFFFF' } };
    const border = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };
    const alignCenter = { vertical: 'middle', horizontal: 'center' };

    // Fetch logos
    try {
      const marabesBase64 = await getBase64FromUrl(marabesLogo);
      const axopeopleBase64 = await getBase64FromUrl(axopeopleLogo);

      const marabesImageId = workbook.addImage({
        base64: marabesBase64.split(',')[1],
        extension: 'png',
      });
      const axopeopleImageId = workbook.addImage({
        base64: axopeopleBase64.split(',')[1],
        extension: 'png',
      });

      sheet.addImage(axopeopleImageId, {
        tl: { col: 0, row: 0 },
        ext: { width: 140, height: 40 }
      });
      sheet.addImage(marabesImageId, {
        tl: { col: 20, row: 0 },
        ext: { width: 140, height: 40 }
      });
    } catch (error) {
      console.error('Error adding logos to Excel:', error);
    }

    const availablePositions = Array.from(new Set(tableData.map(c => c.candidate.position).filter(Boolean)));
    const positionHeaders = availablePositions.length > 0 ? availablePositions : ['Role'];
    const positionColsCount = positionHeaders.length;
    
    const totalCols = 11 + positionColsCount;

    // Add title
    sheet.mergeCells(4, 1, 5, totalCols);
    const titleCell = sheet.getCell(4, 1);
    titleCell.value = 'Overview of Candidates';
    titleCell.font = { size: 16, bold: true, color: { argb: 'FF0F172A' } };
    titleCell.alignment = alignCenter;

    const cardBorder = {
      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
    };

    const card1End = Math.floor(totalCols / 3);
    const card2End = Math.floor((totalCols * 2) / 3);

    // Card 1
    sheet.mergeCells(6, 1, 7, card1End);
    const c1 = sheet.getCell(6, 1);
    c1.value = `Total CVs\nNador (${cityCounts.nador})  |  Tangier (${cityCounts.tangier})`;
    c1.font = { size: 10, bold: true, color: { argb: 'FF0F172A' } };
    c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    c1.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    c1.border = cardBorder;

    // Card 2
    sheet.mergeCells(6, card1End + 1, 7, card2End);
    const c2 = sheet.getCell(6, card1End + 1);
    c2.value = `Pipeline Cities\nNador (${cityCounts.nador})  |  Tangier (${cityCounts.tangier})  |  Other (${cityCounts.other})`;
    c2.font = { size: 10, bold: true, color: { argb: 'FF0EA5E9' } };
    c2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    c2.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    c2.border = cardBorder;

    // Card 3
    sheet.mergeCells(6, card2End + 1, 7, totalCols);
    const c3 = sheet.getCell(6, card2End + 1);
    c3.value = `Academic Background\nMaster (${eduCounts.master})  |  Bachelor (${eduCounts.bachelor})`;
    c3.font = { size: 10, bold: true, color: { argb: 'FF8B5CF6' } };
    c3.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    c3.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    c3.border = cardBorder;

    let currentCol = 4;
    const posStart = currentCol;
    const posEnd = currentCol + positionColsCount - 1;
    currentCol = posEnd + 1;

    const eduStart = currentCol; // 1 column
    const eduEnd = currentCol;
    currentCol++;

    const expStart = currentCol; // 2 columns
    const expEnd = currentCol + 1;
    currentCol += 2;

    const appStart = currentCol; // 1 column for Source
    const appEnd = currentCol;
    currentCol++;

    const resStart = currentCol; // 1 column
    const portStart = currentCol + 1; // 1 column
    const resEnd = portStart;
    currentCol += 2;

    const feedbackCol = currentCol;
    const statusCol = currentCol + 1;

    // Headers
    sheet.getRow(10).values = [
      'Candidats', '', '',
      'Position', ...Array(positionColsCount - 1).fill(''),
      'Education',
      'Professional Experience', '',
      'Applied on',
      'Resume & Portfolio', '',
      'Feedback',
      'Status'
    ];

    sheet.getRow(11).values = [
      'Full Name', 'City', 'Age',
      ...positionHeaders,
      'Diploma : Study Field',
      'Jobs', 'Internship',
      'Source',
      'Resume', 'Portfolio',
      'Description',
      'Status'
    ];

    // Merges
    sheet.mergeCells(10, 1, 10, 3); // Candidats
    if (posEnd > posStart) sheet.mergeCells(10, posStart, 10, posEnd); // Position
    sheet.mergeCells(10, expStart, 10, expEnd); // Experience
    sheet.mergeCells(10, resStart, 10, resEnd); // Resume & Portfolio
    sheet.mergeCells(10, feedbackCol, 11, feedbackCol); // Feedback
    sheet.mergeCells(10, statusCol, 11, statusCol); // Status

    // Apply styles to headers
    [10, 11].forEach(rIdx => {
      const row = sheet.getRow(rIdx);
      row.height = 25;
      row.eachCell((cell, cIdx) => {
        cell.font = headerFont;
        cell.alignment = alignCenter;
        cell.border = border;
        if (rIdx === 10) {
          if ((cIdx >= 1 && cIdx <= 3) || cIdx === eduStart || cIdx === appStart || cIdx === feedbackCol || cIdx === statusCol) {
            cell.fill = headerFill;
          } else {
            cell.fill = headerFillDark;
          }
        } else {
          cell.fill = headerFillDark;
        }
      });
    });

    // Ensure Status and Feedback have right styles since they're merged
    const statusCell = sheet.getCell(10, statusCol);
    statusCell.fill = headerFill;
    statusCell.alignment = alignCenter;
    const feedbackCell = sheet.getCell(10, feedbackCol);
    feedbackCell.fill = headerFill;
    feedbackCell.alignment = alignCenter;

    // Add Data
    let startRow = 12;
    tableData.forEach((rowObj, idx) => {
      const row = sheet.getRow(startRow + idx);
      
      const positionValues = availablePositions.length > 0 
        ? availablePositions.map(pos => rowObj.candidate.position === pos ? '✔' : '')
        : [''];

      const jobsRich = htmlToExcelRichText(rowObj.jobsHtml);
      const internshipRich = htmlToExcelRichText(rowObj.internshipHtml);
      const feedbackRich = htmlToExcelRichText(rowObj.feedbackHtml);

      row.values = [
        rowObj.fullName, rowObj.city, rowObj.age,
        ...positionValues,
        `${rowObj.candidate.diploma || '-'} : ${rowObj.candidate.studyField || '-'}`,
        jobsRich || rowObj.jobs, internshipRich || rowObj.internship,
        rowObj.candidate.appliedFrom || '-',
        rowObj.hasResume ? 'View Link' : '-', 
        rowObj.portfolioLink ? 'Link' : '-',
        feedbackRich || rowObj.feedback,
        (rowObj.status.includes('Hired') || rowObj.status.includes('Approved')) && (rowObj.level || rowObj.candidate?.level)
          ? `${rowObj.status} (${rowObj.level || rowObj.candidate?.level})`
          : rowObj.status
      ];

      row.eachCell((cell, cIdx) => {
        cell.alignment = alignCenter;
        cell.border = border;

        // Link styling for resume
        if (cIdx === resStart && rowObj.hasResume && rowObj.resumeTeamsLink) {
          const url = rowObj.resumeTeamsLink.startsWith('http') ? rowObj.resumeTeamsLink : `https://${rowObj.resumeTeamsLink}`;
          cell.value = { text: 'View Link', hyperlink: url };
          cell.font = { color: { argb: 'FF0000FF' }, underline: true };
        }

        // Link styling for portfolio
        if (cIdx === portStart && rowObj.portfolioLink) {
          const url = rowObj.portfolioLink.startsWith('http') ? rowObj.portfolioLink : `https://${rowObj.portfolioLink}`;
          cell.value = { text: 'Link', hyperlink: url };
          cell.font = { color: { argb: 'FF0000FF' }, underline: true };
        }

      });

      // Left align specific text columns
      row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
      row.getCell(expStart).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      row.getCell(expEnd).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      row.getCell(feedbackCol).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      
      // Left align education columns if they contain text instead of a checkmark
      for (let i = eduStart; i <= eduEnd; i++) {
        const val = row.getCell(i).value;
        if (val && val !== '✔') {
          row.getCell(i).alignment = { vertical: 'middle', horizontal: 'left' };
        }
      }

      // Highlight status column logic
      const sCell = row.getCell(statusCol);
      if (rowObj.status.includes('Hired') || rowObj.status.includes('Approved')) {
        sCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
        sCell.font = { color: { argb: 'FF059669' }, bold: true };
      } else if (rowObj.status.includes('Declined') || rowObj.status.includes('Rejected')) {
        sCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
        sCell.font = { color: { argb: 'FFDC2626' }, bold: true };
      } else {
        sCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        sCell.font = { color: { argb: 'FFD97706' }, bold: true };
      }
    });

    // Column widths
    sheet.columns.forEach((col, idx) => {
      const cIdx = idx + 1; 
      if (cIdx === 1) col.width = 25; // Name
      else if (cIdx === 2) col.width = 15; // City
      else if (cIdx === 3) col.width = 10; // Age
      else if (cIdx >= posStart && cIdx <= eduEnd) col.width = 12; // checks (position + education)
      else if (cIdx >= expStart && cIdx <= expEnd) col.width = 25; // experience
      else if (cIdx >= appStart && cIdx <= appEnd) col.width = 10; // applied
      else if (cIdx === resStart) col.width = 15; // Resume
      else if (cIdx === resEnd) col.width = 15; // Portfolio
      else if (cIdx === feedbackCol) col.width = 35; // Feedback
      else if (cIdx === statusCol) col.width = 20; // Status
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Overview_2026_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToPDF = async () => {
    const doc = new jsPDF('landscape', 'mm', 'a3');
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;

    const headerHeight = 22;
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    const logoWidth = 28;
    const logoHeight = 11;
    const logoY = (headerHeight - logoHeight) / 2;

    try {
      doc.addImage(marabesLogo, 'PNG', margin, logoY, logoWidth, logoHeight);
    } catch (e) { console.error('Error adding marabes logo', e); }

    try {
      doc.addImage(axopeopleLogo, 'PNG', pageWidth - margin - logoWidth, logoY, logoWidth, logoHeight);
    } catch (e) { console.error('Error adding axopeople logo', e); }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('CANDIDATE OVERVIEW', pageWidth / 2, 11.5, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Generated: ${new Date().toLocaleDateString('en-GB')}`,
      pageWidth / 2,
      17.5,
      { align: 'center' }
    );

    autoTable(doc, {
      body: [[
        `Total CVs: Nador (${cityCounts.nador}) - Tangier (${cityCounts.tangier})`,
        `Pipeline Cities: Nador (${cityCounts.nador}) - Tangier (${cityCounts.tangier}) - Other (${cityCounts.other})`,
        `Academic Background: Master (${eduCounts.master}) - Bachelor (${eduCounts.bachelor})`
      ]],
      startY: 22,
      theme: 'grid',
      styles: {
        fontSize: 7,
        halign: 'center',
        valign: 'middle',
        fillColor: [241, 245, 249],
        textColor: [30, 41, 59],
        fontStyle: 'bold',
        lineColor: [203, 213, 225],
        lineWidth: 0.1,
        cellPadding: 2
      },
      margin: { left: 10, right: 10 }
    });

    const availablePositions = Array.from(new Set(tableData.map(c => c.candidate.position).filter(Boolean)));
    const positionHeaders = availablePositions.length > 0 ? availablePositions : ['Role'];
    const positionColsCount = positionHeaders.length;

    const availableEducations = Array.from(new Set(tableData.map(c => c.candidate.diploma).filter(Boolean)));
    const educationHeaders = availableEducations.length > 0 ? availableEducations : ['Diploma'];
    const educationColsCount = educationHeaders.length;

    const head = [
      [
        { content: 'Candidats', colSpan: 3, styles: { fillColor: [30, 41, 59] } },
        { content: 'Position', colSpan: positionColsCount, styles: { fillColor: [15, 23, 42] } },
        { content: 'Education', colSpan: 1, styles: { fillColor: [30, 41, 59] } },
        { content: 'Professional Experience', colSpan: 2, styles: { fillColor: [15, 23, 42] } },
        { content: 'Applied on', colSpan: 1, styles: { fillColor: [30, 41, 59] } },
        { content: 'Resume & Portfolio', colSpan: 2, styles: { fillColor: [15, 23, 42] } },
        { content: 'About Candidate', styles: { fillColor: [30, 41, 59] } },
        { content: 'Status', rowSpan: 2, styles: { fillColor: [30, 41, 59], valign: 'middle' } }
      ],
      [
        'Full Name', 'City', 'Age',
        ...positionHeaders,
        'Diploma : Study Field',
        'Jobs', 'Internship',
        'Source',
        'Resume', 'Portfolio',
        'Description'
      ]
    ];

    const tableRows = [];

    tableData.forEach(row => {
      const positionValues = availablePositions.length > 0
        ? availablePositions.map(pos => row.candidate.position === pos ? 'x' : '')
        : [''];

      const rowIdx = tableRows.length;
      const baseCol = 3 + positionValues.length + 1; // start of experience cols
      const jobsCol = baseCol;
      const internCol = baseCol + 1;
      const feedbackCol = baseCol + 5;

      const candidateData = [
        row.fullName,
        row.city,
        row.age,
        ...positionValues,
        `${row.candidate.diploma || '-'} : ${row.candidate.studyField || '-'}`,
        { content: htmlToPlainText(row.jobsHtml || row.jobs || ''), rawHtml: row.jobsHtml || row.jobs || '' },
        { content: htmlToPlainText(row.internshipHtml || row.internship || ''), rawHtml: row.internshipHtml || row.internship || '' },
        row.candidate.appliedFrom || '-',
        row.hasResume ? { content: 'Link', styles: { textColor: [0, 0, 255] }, url: row.resumeTeamsLink } : '-',
        row.portfolioLink ? { content: 'Link', styles: { textColor: [0, 0, 255] }, url: row.portfolioLink } : '-',
        { content: htmlToPlainText(row.feedbackHtml || row.feedback || ''), rawHtml: row.feedbackHtml || row.feedback || '' },
        (row.status.includes('Hired') || row.status.includes('Approved')) && (row.level || row.candidate?.level)
          ? `${row.status} (${row.level || row.candidate?.level})`
          : row.status
      ];
      tableRows.push(candidateData);
    });

    const dynamicColumnStyles = {
      0: { cellWidth: 35 },
      1: { cellWidth: 15 },
      2: { cellWidth: 8, halign: 'center' },
    };

    const posWidth = Math.max(10, 20 / positionColsCount);
    positionHeaders.forEach((_, i) => {
      dynamicColumnStyles[3 + i] = { cellWidth: posWidth, halign: 'center' };
    });

    let curr = 3 + positionColsCount;
    dynamicColumnStyles[curr++] = { cellWidth: 40, halign: 'left' }; // education

    dynamicColumnStyles[curr++] = { cellWidth: 60 }; // jobs
    dynamicColumnStyles[curr++] = { cellWidth: 60 }; // internship
    dynamicColumnStyles[curr++] = { cellWidth: 18, halign: 'center' }; // Source
    dynamicColumnStyles[curr++] = { cellWidth: 14, halign: 'center' }; // resume
    dynamicColumnStyles[curr++] = { cellWidth: 14, halign: 'center' }; // portfolio
    dynamicColumnStyles[curr++] = { cellWidth: 96 }; // feedback
    dynamicColumnStyles[curr++] = { cellWidth: 20 }; // status

    autoTable(doc, {
      head: head,
      body: tableRows,
      startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : 38,
      theme: 'grid',
      styles: { fontSize: 6, cellPadding: 1, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.1 },
      headStyles: { fillColor: [30, 41, 59], fontSize: 6, halign: 'center', textColor: 255 },
      columnStyles: dynamicColumnStyles,
      rowPageBreak: 'avoid',
      didParseCell: function (data) {
        if (data.cell.raw === 'x' && data.section === 'body') {
          data.cell.text = [''];
        }
      },
      didDrawCell: function (data) {
        if (data.cell.raw === 'x' && data.section === 'body') {
          const doc = data.doc;
          const dim = 3.5;
          const x = data.cell.x + data.cell.width / 2 - dim / 2;
          const y = data.cell.y + data.cell.height / 2 - dim / 2;
          doc.setDrawColor(200, 200, 200);
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(x, y, dim, dim, 0.5, 0.5, 'FD');
          doc.setDrawColor(14, 165, 233);
          doc.setLineWidth(0.4);
          doc.line(x + 0.8, y + 1.8, x + 1.5, y + 2.5);
          doc.line(x + 1.5, y + 2.5, x + 2.8, y + 0.8);
        }

        if (data.section === 'body' && data.cell.raw && data.cell.raw.url) {
          const urlStr = data.cell.raw.url;
          const url = urlStr.startsWith('http') ? urlStr : `https://${urlStr}`;
          data.doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
        }

        if (data.section === 'body' && data.cell.raw && data.cell.raw.rawHtml !== undefined) {
          const doc = data.doc;
          const rawHtml = data.cell.raw.rawHtml;
          if (!rawHtml) return;

          // Paint a white rectangle over autoTable's plain text rendering
          // so we can draw the rich text on top without overlap
          const padL = data.cell.padding('left');
          const padT = data.cell.padding('top');
          const padR = data.cell.padding('right');
          const padB = data.cell.padding('bottom');
          doc.setFillColor(255, 255, 255);
          doc.rect(
            data.cell.x + padL,
            data.cell.y + padT,
            data.cell.width - padL - padR,
            data.cell.height - padT - padB,
            'F'
          );

          // Use the browser DOM to parse HTML — this automatically decodes ALL entities
          const container = document.createElement('div');
          container.innerHTML = rawHtml;

          const ptToMm = 0.352778; // Points to Millimeters conversion
          let cursorX = data.cell.x + data.cell.padding('left');
          // Add the font height in mm to get to the baseline of the first line
          let cursorY = data.cell.y + data.cell.padding('top') + (doc.getFontSize() * ptToMm);
          const maxX = data.cell.x + data.cell.width - data.cell.padding('right');
          const maxY = data.cell.y + data.cell.height - data.cell.padding('bottom');
          const lineHeight = doc.getFontSize() * ptToMm * 1.15;
          const startX = cursorX;

          const setFont = (bold, italic) => {
            let style = 'normal';
            if (bold && italic) style = 'bolditalic';
            else if (bold) style = 'bold';
            else if (italic) style = 'italic';
            doc.setFont('helvetica', style);
          };

          const drawText = (text) => {
            // Replace non-breaking spaces with regular spaces
            text = text.replace(/\u00A0/g, ' ');
            const words = text.split(/(\s+)/);
            for (let i = 0; i < words.length; i++) {
              const word = words[i];
              if (!word) continue;
              const wordWidth = doc.getTextWidth(word);
              if (cursorX + wordWidth > maxX && word.trim() !== '') {
                cursorX = startX;
                cursorY += lineHeight;
              }
              // Safety: don't draw past cell bottom
              if (cursorY > maxY) return;
              if (word.trim() !== '') {
                doc.text(word, cursorX, cursorY);
              }
              cursorX += wordWidth;
            }
          };

          const newLine = () => {
            cursorX = startX;
            cursorY += lineHeight;
          };

          const walkNode = (node, bold, italic) => {
            if (cursorY > maxY) return;
            if (node.nodeType === 3) {
              // Text node — DOM has already decoded all entities
              const text = node.nodeValue || '';
              if (text) {
                setFont(bold, italic);
                drawText(text);
              }
            } else if (node.nodeType === 1) {
              const tag = node.tagName.toUpperCase();
              let b = bold, it = italic;
              if (tag === 'B' || tag === 'STRONG') b = true;
              if (tag === 'I' || tag === 'EM') it = true;
              if (tag === 'BR') { newLine(); return; }
              if (tag === 'LI') { drawText('• '); }
              for (let i = 0; i < node.childNodes.length; i++) {
                if (cursorY > maxY) return;
                walkNode(node.childNodes[i], b, it);
              }
              if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'TR'].includes(tag)) {
                newLine();
              }
            }
          };

          container.childNodes.forEach(child => walkNode(child, false, false));
          // Reset font after drawing cell
          doc.setFont('helvetica', 'normal');
        }
      },
      margin: { top: 30, left: 10, right: 10 }
    });

    doc.save(`Overview_2026_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const thStyle = {
    padding: fitMode ? '0.35rem 0.3rem' : '0.5rem',
    borderBottom: `1px solid ${dm.border}`,
    borderRight: `1px solid ${dm.border}`,
    color: dm.textPrimary,
    fontWeight: '700',
    fontSize: fitMode ? '0.68rem' : '0.75rem',
    textAlign: 'center',
    backgroundColor: dm.headerBg,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0,
    position: 'sticky',
    top: 0,
    zIndex: 3
  };

  const subThStyle = {
    ...thStyle,
    top: fitMode ? '1.75rem' : '2.2rem'
  };

  const tdStyle = {
    padding: fitMode ? '0.28rem 0.3rem' : '0.5rem',
    borderBottom: `1px solid ${dm.border}`,
    borderRight: `1px solid ${dm.border}`,
    color: dm.textSecondary,
    fontSize: fitMode ? '0.7rem' : '0.75rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 0
  };

  const stickyNameStyle = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    boxShadow: `1px 0 0 ${dm.border}`,
    backgroundColor: dm.card
  };

  const stickyStatusStyle = {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    boxShadow: `-1px 0 0 ${dm.border}`,
    backgroundColor: dm.card
  };

  const navButtonStyle = {
    border: `1px solid ${dm.border}`,
    backgroundColor: dm.surface,
    color: dm.textPrimary,
    borderRadius: '0.45rem',
    padding: '0.45rem 0.65rem',
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  };

  return (
    <div style={{ backgroundColor: dm.surface, borderRadius: '1rem', overflow: 'hidden', border: `1px solid ${dm.border}` }}>
      <div style={{ padding: '1.5rem 2rem', borderBottom: `1px solid ${dm.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', backgroundColor: dm.card }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FileSpreadsheet size={24} color={dm.primary} />
            Candidate Overview Table
          </h1>
          <p style={{ margin: '0.25rem 0 0 0', color: dm.textSecondary, fontSize: '0.9rem' }}>
            {tableData.length} Candidates loaded based on active filters.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} color={dm.textSecondary} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search table..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.6rem 1rem 0.6rem 2.5rem',
                borderRadius: '2rem',
                border: `1px solid ${dm.border}`,
                backgroundColor: dm.surface,
                color: dm.textPrimary,
                fontSize: '0.9rem',
                outline: 'none',
                minWidth: '200px'
              }}
            />
          </div>
          <button
            onClick={() => setFitMode((value) => !value)}
            style={{
              ...navButtonStyle,
              backgroundColor: fitMode ? dm.primaryLight : dm.surface,
              color: fitMode ? dm.primary : dm.textPrimary
            }}
          >
            Fit view
          </button>
          <button onClick={exportToPDF} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', borderRadius: '0.5rem', border: `1px solid ${dm.border}`, backgroundColor: 'transparent', color: dm.textPrimary, fontWeight: '600', cursor: 'pointer' }}>
            <FileText size={18} /> Export PDF
          </button>
          <button onClick={exportToExcel} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', borderRadius: '0.5rem', border: 'none', backgroundColor: dm.primary, color: 'white', fontWeight: '600', cursor: 'pointer' }}>
            <Download size={18} /> Export Excel
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div style={{
        padding: '1.5rem 2rem',
        backgroundColor: dm.card,
        borderBottom: `1px solid ${dm.border}`,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
      }}>
        {/* Card 1 */}
        <div style={{
          padding: '1.25rem',
          borderRadius: '1rem',
          backgroundColor: dm.surface,
          border: `1px solid ${dm.border}`,
          boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: dm.textSecondary, fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dm.primary }}></span>
            Total CVs
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline' }}>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: dm.primary, lineHeight: 1 }}>{cityCounts.nador}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Nador</span>
             </div>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: '#0EA5E9', lineHeight: 1 }}>{cityCounts.tangier}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Tangier</span>
             </div>
          </div>
        </div>

        {/* Card 2 */}
        <div style={{
          padding: '1.25rem',
          borderRadius: '1rem',
          backgroundColor: dm.surface,
          border: `1px solid ${dm.border}`,
          boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: dm.textSecondary, fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#0EA5E9' }}></span>
            Pipeline for cities
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline' }}>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: dm.primary, lineHeight: 1 }}>{cityCounts.nador}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Nador</span>
             </div>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: '#0EA5E9', lineHeight: 1 }}>{cityCounts.tangier}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Tangier</span>
             </div>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: '#F59E0B', lineHeight: 1 }}>{cityCounts.other}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Other</span>
             </div>
          </div>
        </div>

        {/* Card 3 */}
        <div style={{
          padding: '1.25rem',
          borderRadius: '1rem',
          backgroundColor: dm.surface,
          border: `1px solid ${dm.border}`,
          boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: dm.textSecondary, fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#8B5CF6' }}></span>
            Academic Background
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'baseline' }}>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: '#8B5CF6', lineHeight: 1 }}>{eduCounts.master}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Master</span>
             </div>
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
               <span style={{ fontSize: '2rem', fontWeight: '800', color: '#F43F5E', lineHeight: 1 }}>{eduCounts.bachelor}</span>
               <span style={{ fontSize: '0.9rem', color: dm.textSecondary, fontWeight: 500 }}>Bachelor</span>
             </div>
          </div>
        </div>
      </div>


      <div
        ref={tableScrollRef}
        style={{
          overflow: 'auto',
          width: '100%',
          backgroundColor: dm.card,
          minHeight: '300px',
          maxHeight: 'calc(100vh - 280px)'
        }}
      >
        {tableData.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: fitMode ? '1000px' : '1500px' }}>
            <colgroup>
              <col style={{ width: fitMode ? '130px' : '210px' }} />
              <col style={{ width: fitMode ? '80px' : '130px' }} />
              <col style={{ width: fitMode ? '48px' : '80px' }} />

              <col style={{ width: fitMode ? '140px' : '180px' }} />
              <col style={{ width: fitMode ? '140px' : '180px' }} />
              <col style={{ width: fitMode ? '140px' : '180px' }} />

              <col style={{ width: fitMode ? '130px' : '180px' }} />
              <col style={{ width: fitMode ? '130px' : '180px' }} />

              <col style={{ width: fitMode ? '110px' : '140px' }} />

              <col style={{ width: fitMode ? '70px' : '120px' }} />
              <col style={{ width: fitMode ? '70px' : '120px' }} />
              <col style={{ width: fitMode ? '150px' : '280px' }} />
              <col style={{ width: fitMode ? '120px' : '190px' }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan="3" style={{ ...thStyle, backgroundColor: isDark ? '#1E293B' : '#E2E8F0', color: dm.primary, fontSize: fitMode ? '0.78rem' : '0.95rem' }}>Candidats</th>
                <th colSpan="1" style={{ ...thStyle, backgroundColor: isDark ? '#0F172A' : '#F1F5F9' }}>Position</th>
                <th colSpan="2" style={{ ...thStyle, backgroundColor: isDark ? '#1E293B' : '#E2E8F0' }}>Education</th>
                <th colSpan="2" style={{ ...thStyle, backgroundColor: isDark ? '#0F172A' : '#F1F5F9' }}>Professional Experience</th>
                <th colSpan="1" style={{ ...thStyle, backgroundColor: isDark ? '#1E293B' : '#E2E8F0', color: dm.primary, fontSize: fitMode ? '0.78rem' : '0.95rem' }}>Applied on</th>
                <th colSpan="2" style={{ ...thStyle, backgroundColor: isDark ? '#0F172A' : '#F1F5F9', color: dm.primary, fontSize: fitMode ? '0.78rem' : '0.95rem' }}>Resume & Portfolio</th>
                <th style={{ ...thStyle, backgroundColor: isDark ? '#1E293B' : '#E2E8F0' }}>About Candidate</th>
                <th rowSpan="2" style={{ ...thStyle, ...stickyStatusStyle, backgroundColor: dm.headerBg, verticalAlign: 'middle', borderRight: 'none', zIndex: 5 }}>Status</th>
              </tr>
              <tr>
                <th style={{ ...subThStyle, ...stickyNameStyle, borderLeft: 'none', zIndex: 5, backgroundColor: dm.headerBg }}>Full Name</th>
                <th style={subThStyle}>City</th>
                <th style={subThStyle}>Age</th>

                <th style={subThStyle}>Role</th>
                <th style={subThStyle}>Diploma</th>
                <th style={subThStyle}>Study Field</th>

                <th style={subThStyle}>Jobs</th>
                <th style={subThStyle}>Internship</th>

                <th style={subThStyle}>Source</th>

                <th style={subThStyle}>Resume</th>
                <th style={subThStyle}>Portfolio</th>

                <th style={subThStyle}>Description</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, idx) => {
                const rowBg = idx % 2 === 0 ? dm.card : dm.surface;
                return (
                  <tr key={row.id} style={{
                    backgroundColor: rowBg,
                    transition: 'background-color 0.2s',
                  }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = dm.rowHover}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = rowBg}
                  >
                    <td style={{ ...tdStyle, ...stickyNameStyle, backgroundColor: rowBg, color: dm.textPrimary, fontWeight: '600', textAlign: 'left' }} title={row.fullName}>{row.fullName}</td>
                    <td style={tdStyle}>{renderEditableText(row, 'city', 'city')}</td>
                    <td style={tdStyle}>{renderEditableText(row, 'age', 'age', { type: 'number' })}</td>

                    <td style={tdStyle}>{renderEditableSelect(row, 'position', POSITION_OPTIONS, 'position')}</td>
                    <td style={tdStyle}>{renderEditableSelect(row, 'diploma', ['Master', 'Engineer', 'Bachelor', 'High School Diploma', 'Specialized Technician'], 'diploma')}</td>
                    <td style={tdStyle}>{renderEditableText(row, 'studyField', 'studyField')}</td>

                    <td style={{ ...tdStyle, fontWeight: '500', whiteSpace: 'normal', verticalAlign: 'top' }}>{renderEditableOverlay(row, 'experience', 'Professional Experience (Jobs)', 'experience')}</td>
                    <td style={{ ...tdStyle, fontWeight: '500', whiteSpace: 'normal', verticalAlign: 'top' }}>{renderEditableOverlay(row, 'internship', 'Professional Experience (Internship)', 'internship')}</td>

                    <td style={tdStyle}>{renderEditableSelect(row, 'appliedFrom', ['Indeed', 'LinkedIn', 'Email'], 'appliedFrom')}</td>

                    <td style={tdStyle}>
                      {renderEditableLink(row, 'resumeTeamsLink', 'resumeTeamsLink')}
                    </td>
                    <td style={tdStyle}>
                      {row.portfolioLink ? (
                        <a href={row.portfolioLink} target="_blank" rel="noopener noreferrer" style={{ color: dm.primary, textDecoration: 'underline' }}>{row.portfolio}</a>
                      ) : (<span style={{ color: dm.border }}>-</span>)}
                    </td>

                    <td style={{ ...tdStyle, textAlign: 'left', whiteSpace: 'normal', overflow: 'hidden', verticalAlign: 'top' }}>
                      {renderEditableOverlay(row, 'description', 'About Candidate', 'description')}
                    </td>

                    <td style={{ ...tdStyle, ...stickyStatusStyle, backgroundColor: rowBg, borderRight: 'none' }}>{getStatusBadge(row.status, row.level, row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '3rem', textAlign: 'center', color: dm.textSecondary }}>
            No candidates match the current filters or search term.
          </div>
        )}
      </div>


      {editingField && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: dm.card,
            padding: '1.5rem',
            borderRadius: '0.5rem',
            width: '90%',
            maxWidth: '600px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: `1px solid ${dm.border}`
          }}>
            <h2 style={{ marginTop: 0, color: dm.textPrimary, fontSize: '1.25rem', marginBottom: '1rem' }}>Edit {editingField.label}</h2>
            {['description', 'experience', 'internship'].includes(editingField.field) ? (
              <div style={{ minHeight: '300px' }}>
                <RichTextEditor
                  value={editingField.value}
                  onChange={(val) => setEditingField({ ...editingField, value: val })}
                />
              </div>
            ) : (
              <textarea
                autoFocus
                value={editingField.value}
                onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: '0.75rem',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit',
                  border: `1px solid ${dm.border}`,
                  borderRadius: '0.35rem',
                  backgroundColor: dm.surface,
                  color: dm.textPrimary,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => setEditingField(null)}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.35rem', border: `1px solid ${dm.border}`, backgroundColor: dm.surface, color: dm.textPrimary, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  commitCandidateField(editingField.row, { [editingField.field]: editingField.value }, editingField.cellKey);
                  setEditingField(null);
                }}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.35rem', border: 'none', backgroundColor: dm.primary, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateOverviewTable;
